import { supabase } from '../../lib/supabase'
import type { Category } from '../../components/agent/scheduleDay'
import { getEmployeeTeamMap } from './leaveRequestQueue'

// The real counterpart to SupervisorPlanningBoardPage.tsx's own
// approvedLeaveByCellFor, for a schedule submission
// (20260922000000_schedule_submissions.sql) rather than a leave request:
// once a supervisor approves one, the pay-period entries
// muster.submit_schedule already wrote to schedule_entries at submission
// time (real regardless of decision, this migration's own header comment)
// become the board's own real content for that employee's row, the same
// way an approved leave request already does. schedule_entries carries no
// schedule_submission_id of its own (the same migration's own
// reconciliation note), so there is no direct join: this reads every
// approved submission's own employee and period range, then every
// schedule_entries row for those employees, and keeps only the ones that
// fall inside an approved range, the same filter a direct join would have
// applied.
export type ApprovedScheduleDay = { category: Category; label: string; detail?: string; countsAsAvailable?: boolean }

type SubmissionRow = { employee_id: string; period_start: string; period_end: string }

export type EntryRow = {
  employee_id: string
  date: string
  start_time: string | null
  end_time: string | null
  status_codes: { name: string; category: Category; counts_as_available: boolean } | null
  assignments: { name: string } | null
}

// "07:00:00" (a Postgres `time`) becomes "0700": the board's own raw,
// 24-hour label form (scheduleDay.ts's own scheduledHours regex), not
// something already formatted for display.
function timeLabel(startTime: string, endTime: string): string {
  const strip = (value: string) => value.slice(0, 5).replace(':', '')
  return `${strip(startTime)}–${strip(endTime)}`
}

// Mirrors boardCodes.ts's own BOARD_CODES shape for each of the four
// categories a real submission can actually carry (scheduleSubmissionStore.ts's
// own statusCodeNameFor): a work or training entry's label leads with its
// own real time, not the fixed shift template every mock roster day
// carries, since an agent's real submission is free to differ from it. Off
// and leave read the status code's own name directly, the same as
// boardCodes.ts does for both.
export function dayFor(entry: EntryRow): ApprovedScheduleDay | null {
  const day = categoryDayFor(entry)
  return day && entry.status_codes ? { ...day, countsAsAvailable: entry.status_codes.counts_as_available } : day
}

function categoryDayFor(entry: EntryRow): ApprovedScheduleDay | null {
  const statusCode = entry.status_codes
  if (!statusCode) return null
  const hasTimes = Boolean(entry.start_time && entry.end_time)

  switch (statusCode.category) {
    case 'work':
      return {
        category: 'work',
        label: hasTimes ? timeLabel(entry.start_time as string, entry.end_time as string) : statusCode.name,
        detail: entry.assignments?.name,
      }
    case 'training':
      return {
        category: 'training',
        label: hasTimes ? `${timeLabel(entry.start_time as string, entry.end_time as string)} · ${statusCode.name}` : statusCode.name,
      }
    case 'leave':
      return { category: 'leave', label: statusCode.name, detail: 'Full day' }
    default:
      return { category: statusCode.category, label: statusCode.name }
  }
}

export const SCHEDULE_ENTRY_COLUMNS = 'employee_id, date, start_time, end_time, status_codes(name, category, counts_as_available), assignments(name)'

// Every day of an approved schedule submission a signed-in supervisor (or
// administrator) can see, keyed the same way approvedLeaveByCellFor's own
// map is: employee id and ISO date, so SupervisorPlanningBoardPage.tsx
// can look one up per cell regardless of which static roster file, if
// any, that employee's row otherwise comes from.
export async function getApprovedScheduleDays(): Promise<Map<string, ApprovedScheduleDay>> {
  const { data: submissions, error: submissionsError } = await supabase
    .from('schedule_submissions')
    .select('employee_id, period_start, period_end')
    .eq('state', 'approved')
  if (submissionsError || !submissions || submissions.length === 0) return new Map()

  const approvedRanges = new Map<string, { start: string; end: string }[]>()
  for (const row of submissions as unknown as SubmissionRow[]) {
    const ranges = approvedRanges.get(row.employee_id) ?? []
    ranges.push({ start: row.period_start, end: row.period_end })
    approvedRanges.set(row.employee_id, ranges)
  }

  const [employeeTeams, entriesResult] = await Promise.all([
    getEmployeeTeamMap(),
    supabase
      .from('schedule_entries')
      .select(SCHEDULE_ENTRY_COLUMNS)
      .in('employee_id', Array.from(approvedRanges.keys())),
  ])
  const { data: entries, error: entriesError } = entriesResult
  if (entriesError || !entries) return new Map()

  const approvedScheduleByCell = new Map<string, ApprovedScheduleDay>()
  for (const row of entries as unknown as EntryRow[]) {
    const teamInfo = employeeTeams.get(row.employee_id)
    if (!teamInfo) continue
    const ranges = approvedRanges.get(row.employee_id) ?? []
    if (!ranges.some((range) => row.date >= range.start && row.date <= range.end)) continue
    const day = dayFor(row)
    if (!day) continue
    approvedScheduleByCell.set(`${row.employee_id}|${row.date}`, day)
  }
  return approvedScheduleByCell
}
